<template>
  <div id="app">
    <div class="podcast-container" :class="{ 'theme--dark': darkMode }">
      <div class="podcast-card">
        <div class="cosmic-background">
          <div class="neural-grid"></div>
        </div>

        <div class="speakers-row">
          <div
            v-for="(speaker, index) in speakers"
            :key="index"
            class="speaker-avatar-container"
            :class="{ 'active-speaker': currentSpeakerIndex === index }"
            @click="selectSpeaker(index)"
          >
            <img :src="speaker.avatar" alt="" class="speaker-avatar">
            <div class="pulse-rings" v-if="currentSpeakerIndex === index">
              <div class="ring"></div>
              <div class="ring"></div>
            </div>
          </div>
        </div>

        <div class="wave-container">
          <div class="wave-visualization">
            <div class="wave-graph">
              <svg width="100%" height="100%" viewBox="0 0 1000 200" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stop-color="#818cf8" stop-opacity="0.7" />
                    <stop offset="100%" stop-color="#818cf8" stop-opacity="0.1" />
                  </linearGradient>
                </defs>

                <!-- Background grid lines -->
                <g class="grid-lines">
                  <line v-for="n in 10" :key="'h-'+n" x1="0" :y1="n * 20" x2="1000" :y2="n * 20" />
                  <line v-for="n in 20" :key="'v-'+n" :x1="n * 50" y1="0" :x2="n * 50" y2="200" />
                </g>

                <!-- Wave path -->
                <path :d="getWavePath()" fill="url(#waveGradient)" />

                <!-- Wave line -->
                <path :d="getWavePath()" stroke="#818cf8" stroke-width="2" fill="none" />

                <!-- Data points -->
                <g v-if="isPlaying">
                  <circle
                    v-for="(point, i) in getWavePoints()"
                    :key="i"
                    :cx="point.x"
                    :cy="point.y"
                    r="3"
                    fill="#818cf8"
                  />
                </g>
              </svg>
            </div>
          </div>

          <div class="controls">
            <div
              class="play-button"
              :class="{ 'playing': isPlaying }"
              @click="togglePlayback"
            >
              <div class="play-icon"></div>
              <div class="pause-icon"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PodcastAnimation',
  data() {
    return {
      isPlaying: false,
      currentSpeakerIndex: 0,
      speakers: [
        {
          name: "Sam Kokoro",
          avatar: '/static/Gemini_Generated_Image_uobiifuobiifuobi.jpeg'
        },
        {
          name: "Alex Chen",
          avatar: '/static/Gemini_Generated_Image_v3kyixv3kyixv3ky.png'
        },
        {
          name: "Jordan Singh",
          avatar: '/static/Gemini_Generated_Image_b8negjb8negjb8ne.jpeg'
        },
        {
          name: "Taylor Rivera",
          avatar: '/static/Gemini_Generated_Image_ns59fwns59fwns59.jpeg'
        }
      ],
      animationTimer: null,
      wavePoints: [],
      waveAmplitude: 70,
      waveFrequency: 0.015,
      wavePhase: 0,
      baseHeight: 100,
      darkMode: true,
    };
  },

  mounted() {
    // Initialize wave animation
    this.initWavePoints();

    // Start animation loop
    this.startWaveAnimation();
  },

  beforeDestroy() {
    // Clean up timer
    cancelAnimationFrame(this.animationTimer);
  },

  methods: {
    togglePlayback() {
      this.isPlaying = !this.isPlaying;

      if (this.isPlaying) {
        this.waveAmplitude = 70;
      } else {
        this.waveAmplitude = 30;
      }
    },

    selectSpeaker(index) {
      this.currentSpeakerIndex = index;
    },

    initWavePoints() {
      this.wavePoints = [];
      const totalPoints = 100;

      for (let i = 0; i < totalPoints; i++) {
        this.wavePoints.push({
          x: (i / (totalPoints - 1)) * 1000,
          y: this.baseHeight
        });
      }
    },

    startWaveAnimation() {
      const animate = () => {
        this.updateWavePoints();
        this.animationTimer = requestAnimationFrame(animate);
      };

      this.animationTimer = requestAnimationFrame(animate);
    },

    updateWavePoints() {
      // Update phase for wave movement
      this.wavePhase += this.isPlaying ? 0.05 : 0.02;

      // Update each point in the wave
      for (let i = 0; i < this.wavePoints.length; i++) {
        const x = this.wavePoints[i].x;
        const relativeX = x / 1000; // Normalize to 0-1 range

        // Create a complex wave with multiple sine components
        const amplitude = this.isPlaying ? this.waveAmplitude : 30;

        // Base sine wave
        let y = Math.sin(relativeX * 12 + this.wavePhase) * amplitude;

        // Add some harmonics for more interesting shape
        y += Math.sin(relativeX * 24 + this.wavePhase * 1.1) * (amplitude * 0.3);
        y += Math.sin(relativeX * 36 + this.wavePhase * 0.7) * (amplitude * 0.15);

        // Add some noise for realism
        y += (Math.random() * 10 - 5) * (this.isPlaying ? 1 : 0.5);

        // Center around the base height
        y = this.baseHeight - y;

        this.wavePoints[i].y = y;
      }
    },

    getWavePath() {
      if (!this.wavePoints.length) return '';

      // Start at the first point
      let path = `M ${this.wavePoints[0].x} ${this.wavePoints[0].y}`;

      // Add line segments to each subsequent point
      for (let i = 1; i < this.wavePoints.length; i++) {
        path += ` L ${this.wavePoints[i].x} ${this.wavePoints[i].y}`;
      }

      // Complete the path by extending to the bottom corners
      path += ` L ${this.wavePoints[this.wavePoints.length - 1].x} 200`;
      path += ` L 0 200`;
      path += ` Z`;

      return path;
    },

    getWavePoints() {
      // Return a subset of points for rendering circles
      return this.wavePoints.filter((_, i) => i % 5 === 0);
    }
  }
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  background-color: #f8fafc;
  color: #1e293b;
}

.podcast-container {
  position: relative;
  width: 100%;
  max-width: 1200px;
  height: 400px;
  display: flex;
  flex-direction: column;
  z-index: 2;
}

.podcast-card {
  background: #ffffff;
  backdrop-filter: blur(12px);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(226, 232, 240, 0.7);
  box-shadow:
    0 4px 20px rgba(15, 23, 42, 0.08),
    0 0 0 1px rgba(148, 163, 184, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.cosmic-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
  opacity: 0.04;
  pointer-events: none;
  z-index: -1;
}

.neural-grid {
  position: absolute;
  width: 100%;
  height: 100%;
  background:
    linear-gradient(90deg, rgba(99, 102, 241, 0.1) 1px, transparent 1px),
    linear-gradient(0deg, rgba(99, 102, 241, 0.1) 1px, transparent 1px);
  background-size: 20px 20px;
  animation: gridFloat 60s linear infinite;
}

.speakers-row {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.speaker-avatar-container {
  position: relative;
  width: 70px;
  height: 70px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 16px;
  overflow: hidden;
}

.speaker-avatar-container:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
}

.active-speaker {
  transform: translateY(-8px) scale(1.05);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
}

.speaker-avatar {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  object-fit: cover;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.active-speaker .speaker-avatar {
  border-color: #818cf8;
}

.pulse-rings {
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  z-index: 1;
  pointer-events: none;
}

.ring {
  position: absolute;
  border-radius: 16px;
  border: 2px solid #818cf8;
  opacity: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.ring:nth-child(2) {
  animation-delay: 1s;
}

.wave-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}

.wave-visualization {
  flex-grow: 1;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(249, 250, 251, 0.5);
  border: 1px solid rgba(226, 232, 240, 0.8);
  position: relative;
}

.wave-graph {
  width: 100%;
  height: 100%;
  position: relative;
}

.grid-lines line {
  stroke: rgba(203, 213, 225, 0.6);
  stroke-width: 1;
}

.controls {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.play-button {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(145deg, #f8fafc, #e2e8f0);
  border: 2px solid rgba(203, 213, 225, 0.6);
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.play-button:hover {
  transform: scale(1.05);
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.1);
}

.play-button:active {
  transform: scale(0.98);
}

.play-icon, .pause-icon {
  position: absolute;
  transition: all 0.3s ease;
}

.play-icon {
  border-style: solid;
  border-width: 15px 0 15px 25px;
  border-color: transparent transparent transparent #818cf8;
  margin-left: 5px;
  opacity: 1;
}

.pause-icon {
  width: 8px;
  height: 25px;
  background: #818cf8;
  margin-right: 13px;
  opacity: 0;
  box-shadow: 15px 0 0 #818cf8;
}

.play-button.playing .play-icon {
  opacity: 0;
}

.play-button.playing .pause-icon {
  opacity: 1;
}

/* Animation keyframes */
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

@keyframes gridFloat {
  0% { transform: translateX(0) translateY(0); }
  100% { transform: translateX(-20px) translateY(-20px); }
}

/* Theme toggle for dark mode */
.theme--dark .podcast-card {
  background: #1e293b;
  border-color: rgba(51, 65, 85, 0.7);
  box-shadow:
    0 4px 20px rgba(15, 23, 42, 0.3),
    0 0 0 1px rgba(51, 65, 85, 0.3);
}

.theme--dark .neural-grid {
  background:
    linear-gradient(90deg, rgba(99, 102, 241, 0.15) 1px, transparent 1px),
    linear-gradient(0deg, rgba(99, 102, 241, 0.15) 1px, transparent 1px);
}

.theme--dark .wave-visualization {
  background: rgba(30, 41, 59, 0.4);
  border-color: rgba(51, 65, 85, 0.8);
}

.theme--dark .grid-lines line {
  stroke: rgba(71, 85, 105, 0.6);
}

.theme--dark .play-button {
  background: linear-gradient(145deg, #1e293b, #0f172a);
  border-color: rgba(51, 65, 85, 0.6);
}

.theme--dark .speaker-avatar {
  border-color: #6366f1;
}

.theme--dark .active-speaker .speaker-avatar {
  border-color: #818cf8;
}

.theme--dark .ring {
  border-color: #818cf8;
}

/* Responsive design */
@media (max-width: 768px) {
  .podcast-container {
    padding: 1rem;
    height: 350px;
  }

  .speakers-row {
    gap: 1rem;
  }

  .speaker-avatar-container {
    width: 50px;
    height: 50px;
  }

  .play-button {
    width: 60px;
    height: 60px;
  }

  .play-icon {
    border-width: 12px 0 12px 20px;
  }

  .pause-icon {
    width: 6px;
    height: 20px;
    box-shadow: 12px 0 0 #818cf8;
  }
}
</style>
